import React from 'react';
import { Helmet } from 'react-helmet-async';

interface OrganizationSchemaProps {
  type?: 'organization' | 'faq' | 'breadcrumb';
}

export const OrganizationSchema: React.FC = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EU AI Act Training Platform",
    "url": "https://ki-kompetenznachweis.online",
    "logo": "https://ki-kompetenznachweis.online/logo.svg",
    "description": "Professionelle Online-Schulungen für EU AI Act Compliance und KI-Kompetenznachweis",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "DE"
    },
    "sameAs": []
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const FAQSchema: React.FC<{ faqs: Array<{ question: string; answer: string }> }> = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const BreadcrumbSchema: React.FC<{ items: Array<{ name: string; url: string }> }> = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://ki-kompetenznachweis.online${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const CourseSchema: React.FC = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "EU AI Act Kompetenznachweis - Online-Schulung",
    "description": "Lernen Sie in 45 Minuten die Anforderungen des EU AI Act zu verstehen und anzuwenden. Inklusive Zertifikat.",
    "provider": {
      "@type": "Organization",
      "name": "EU AI Act Training Platform",
      "url": "https://ki-kompetenznachweis.online"
    },
    "courseMode": "online",
    "educationalLevel": "Beginner to Intermediate",
    "inLanguage": "de",
    "teaches": "EU AI Act Compliance, KI-Kompetenz, Künstliche Intelligenz Grundlagen",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": "PT45M",
      "instructor": {
        "@type": "Organization",
        "name": "EU AI Act Training Platform"
      }
    },
    "offers": {
      "@type": "Offer",
      "price": "49.90",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": "https://ki-kompetenznachweis.online/registrieren",
      "category": "Paid"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const VideoSchema: React.FC<{
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration: string;
}> = ({ name, description, thumbnailUrl, uploadDate, duration }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": name,
    "description": description,
    "thumbnailUrl": thumbnailUrl,
    "uploadDate": uploadDate,
    "duration": duration,
    "contentUrl": "https://ki-kompetenznachweis.online",
    "embedUrl": "https://ki-kompetenznachweis.online"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};
